import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Mail, Loader2, ShieldCheck, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import BrandLogo from "@/components/BrandLogo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Second-factor step shown after a primary login when MFA is on.
function MfaChallenge({ info, onVerified }) {
  const [method, setMethod] = useState("totp"); // totp | email | recovery
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [emailSent, setEmailSent] = useState(false);

  const sendEmail = async () => {
    setError(""); setBusy(true);
    try { await api.post("/mfa/email/send"); setEmailSent(true); }
    catch (e) { setError(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const verify = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    const path = method === "totp" ? "/mfa/verify/totp" : method === "email" ? "/mfa/verify/email" : "/mfa/verify/recovery";
    try {
      const res = await api.post(path, { code: code.trim() });
      onVerified(res.data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const Tab = ({ id, label, icon: Icon }) => (
    <button type="button" data-testid={`mfa-method-${id}`} onClick={() => { setMethod(id); setError(""); setCode(""); }}
      className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-medium py-2 rounded-md transition ${method === id ? "bg-[#003E87] text-white" : "text-slate-500 hover:bg-slate-100"}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );

  return (
    <div data-testid="mfa-challenge" className="mt-8">
      <div className="flex items-center gap-2 text-[#003E87] font-semibold"><ShieldCheck className="w-5 h-5" /> Two-factor verification</div>
      <p className="mt-1 text-sm text-slate-500">Your account is protected with MFA. Confirm it's you to finish signing in.</p>

      <div className="mt-5 flex gap-1 bg-slate-50 border border-slate-200 rounded-lg p-1">
        <Tab id="totp" label="Authenticator" icon={ShieldCheck} />
        <Tab id="email" label="Email code" icon={Mail} />
        <Tab id="recovery" label="Recovery" icon={KeyRound} />
      </div>

      <form onSubmit={verify} className="mt-4 space-y-3">
        {method === "email" && (
          <div className="text-xs text-slate-500">
            {emailSent ? `We sent a 6-digit code to ${info?.email_hint || "your email"}.`
              : `We'll email a one-time code to ${info?.email_hint || "your email"}.`}
            <button type="button" data-testid="mfa-send-email-btn" onClick={sendEmail} disabled={busy}
              className="ml-1 text-[#003E87] font-medium hover:underline">{emailSent ? "Resend" : "Send code"}</button>
          </div>
        )}
        <div>
          <Label className="text-xs text-slate-500">{method === "recovery" ? "Recovery code" : "6-digit code"}</Label>
          <Input data-testid="mfa-code-input" autoFocus value={code}
            inputMode={method === "recovery" ? "text" : "numeric"}
            onChange={(e) => setCode(method === "recovery" ? e.target.value : e.target.value.replace(/\D/g, "").slice(0, 6))}
            className="mt-1 h-11 tracking-widest font-mono"
            placeholder={method === "recovery" ? "xxxxxx-xxxxxx" : "123456"} />
        </div>
        {error && <div data-testid="mfa-error" className="text-xs text-rose-600 bg-rose-50 rounded px-3 py-2">{error}</div>}
        <Button data-testid="mfa-verify-btn" type="submit" disabled={busy || !code.trim()}
          className="h-11 w-full bg-[#F4C400] hover:bg-[#e6b000] text-black rounded-lg text-sm font-bold">
          {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Verify &amp; sign in
        </Button>
      </form>
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [params] = useSearchParams();
  const rawNext = params.get("next") || "";
  const nextPath = rawNext.startsWith("/") && !rawNext.startsWith("//") ? rawNext : "/dashboard";

  // Pin the GridForge mark exactly on the sun. The panel is landscape but the photo is
  // portrait, so object-cover crops it — replicate that math to keep the mark centered on
  // the sun (normalized 0.525, 0.424 in the source image) at any viewport size.
  const panelRef = useRef(null);
  const imgRef = useRef(null);
  const markRef = useRef(null);
  const SUN = { x: 0.525, y: 0.424 };
  const placeMark = useCallback(() => {
    const c = panelRef.current, img = imgRef.current, mark = markRef.current;
    if (!c || !img || !mark) return;
    const cw = c.clientWidth, ch = c.clientHeight;
    const iw = img.naturalWidth || 1400, ih = img.naturalHeight || 2000;
    const scale = Math.max(cw / iw, ch / ih);
    const dw = iw * scale, dh = ih * scale;
    mark.style.left = `${(cw - dw) / 2 + SUN.x * dw}px`;
    mark.style.top = `${(ch - dh) / 2 + SUN.y * dh}px`;
  }, [SUN.x, SUN.y]);
  useEffect(() => {
    placeMark();
    window.addEventListener("resize", placeMark);
    return () => window.removeEventListener("resize", placeMark);
  }, [placeMark]);

  const [mode, setMode] = useState("signin"); // signin | register | magic | forgot
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [magicSent, setMagicSent] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [mfa, setMfa] = useState(null); // { methods, email_hint } when a challenge is pending

  const google = () => {
    if (nextPath !== "/dashboard") sessionStorage.setItem("gf_next", nextPath);
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const finishLogin = (user) => { setUser(user); navigate(nextPath); };

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      if (mode === "magic") {
        await api.post("/auth/magic/request", { email }); setMagicSent(true);
      } else if (mode === "forgot") {
        await api.post("/auth/password/reset-request", { email }); setResetSent(true);
      } else {
        const path = mode === "register" ? "/auth/register" : "/auth/login";
        const payload = mode === "register" ? { email, password, name } : { email, password };
        const res = await api.post(path, payload);
        if (res.data.mfa_required) setMfa(res.data);
        else finishLogin(res.data.user);
      }
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white text-slate-900">
      {/* Left — sign in */}
      <div className="relative flex flex-col px-8 sm:px-16 py-10">
        <div className="max-w-sm w-full mx-auto">
          <BrandLogo size={44} className="justify-center" />
        </div>
        <div className="max-w-sm w-full mx-auto flex-1 flex flex-col justify-center pt-8">

          {mfa ? (
            <>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight leading-[1.05] text-black">
                One more step
              </h1>
              <MfaChallenge info={mfa} onVerified={finishLogin} />
              <button data-testid="mfa-back-button" onClick={() => { setMfa(null); setPassword(""); }}
                className="mt-5 text-xs text-slate-400 hover:text-slate-700 underline underline-offset-2">
                Back to sign in
              </button>
            </>
          ) : (magicSent || resetSent) ? (
            <div data-testid="magic-sent-message" className="mt-2 rounded-lg border border-[#F4C400]/40 bg-[#F4C400]/10 p-5 text-sm text-slate-700">
              <div className="font-semibold text-[#8a6d00] mb-1">Check your email</div>
              If <span className="font-medium text-black">{email}</span> has an account, a {resetSent ? "password reset" : "one-time sign-in"} link is on its
              way. It expires in {resetSent ? "30" : "15"} minutes.
              <button data-testid="magic-back-button" onClick={() => { setMagicSent(false); setResetSent(false); setMode("signin"); }}
                className="block mt-3 text-slate-500 hover:text-black underline underline-offset-2">
                Back to sign in
              </button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight leading-[1.05] text-black text-center">
                Sign in
              </h1>
              <p className="mt-3 text-base text-slate-500 text-center">Access is by invitation. Use your work email.</p>

              <Button data-testid="login-google-button" onClick={google}
                className="mt-8 h-11 px-5 bg-[#003E87] hover:bg-[#00305F] text-white rounded-lg w-full text-sm font-semibold">
                Continue with Google <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <div className="my-5 flex items-center gap-3 text-[11px] uppercase tracking-widest text-slate-400">
                <span className="h-px flex-1 bg-slate-200" /> or <span className="h-px flex-1 bg-slate-200" />
              </div>

              <form onSubmit={submit} className="space-y-3">
                {mode === "register" && (
                  <div>
                    <Label className="text-xs text-slate-500">Name</Label>
                    <Input data-testid="login-name-input" value={name} onChange={(e) => setName(e.target.value)}
                      className="mt-1 h-11" placeholder="Your name" />
                  </div>
                )}
                <div>
                  <Label className="text-xs text-slate-500">Work email</Label>
                  <Input data-testid="login-email-input" type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)} className="mt-1 h-11" placeholder="you@company.com" />
                </div>
                {(mode === "signin" || mode === "register") && (
                  <div>
                    <Label className="text-xs text-slate-500">Password</Label>
                    <Input data-testid="login-password-input" type="password" required value={password}
                      onChange={(e) => setPassword(e.target.value)} className="mt-1 h-11"
                      placeholder={mode === "register" ? "At least 8 characters" : "Your password"} />
                  </div>
                )}

                {error && (
                  <div data-testid="login-error" className="text-xs text-rose-600 bg-rose-50 rounded px-3 py-2">{error}</div>
                )}

                <Button data-testid="login-submit-button" type="submit" disabled={busy}
                  className="h-11 w-full bg-[#F4C400] hover:bg-[#e6b000] text-[#000000] rounded-lg text-sm font-bold">
                  {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {mode === "register" ? "Create account" : mode === "magic" ? "Email me a sign-in link" : mode === "forgot" ? "Email me a reset link" : "Sign in"}
                </Button>
              </form>

              <div className="mt-5 space-y-2 text-xs text-slate-500">
                {mode !== "magic" ? (
                  <button data-testid="login-magic-toggle" onClick={() => { setMode("magic"); setError(""); }}
                    className="flex items-center gap-1.5 hover:text-black">
                    <Mail className="w-3.5 h-3.5" /> Email me a one-time sign-in link instead
                  </button>
                ) : (
                  <button data-testid="login-password-toggle" onClick={() => { setMode("signin"); setError(""); }} className="hover:text-black">
                    Use a password instead
                  </button>
                )}
                {mode === "signin" && (
                  <button data-testid="login-register-toggle" onClick={() => { setMode("register"); setError(""); }} className="block hover:text-black">
                    First time here? Set a password
                  </button>
                )}
                {mode === "signin" && (
                  <button data-testid="login-forgot-toggle" onClick={() => { setMode("forgot"); setError(""); }} className="block hover:text-black">
                    Forgot your password?
                  </button>
                )}
                {mode === "forgot" && (
                  <button data-testid="login-forgot-back" onClick={() => { setMode("signin"); setError(""); }} className="block hover:text-black">
                    Back to sign in
                  </button>
                )}
                {mode === "register" && (
                  <button data-testid="login-signin-toggle" onClick={() => { setMode("signin"); setError(""); }} className="block hover:text-black">
                    Already have an account? Sign in
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right — brand panel: our mark sits on the sun so the rays read as GridForge's */}
      <div ref={panelRef} className="hidden lg:block relative overflow-hidden bg-slate-900 border-l border-slate-100">
        <img ref={imgRef} src="/login-solar.webp" onLoad={placeMark}
          alt="Utility-scale solar array" className="absolute inset-0 w-full h-full object-cover" />
        <img ref={markRef} src="/gridforge-mark.png" alt="" aria-hidden="true" data-testid="login-sun-mark"
          className="absolute -translate-x-1/2 -translate-y-1/2 w-40 h-40 xl:w-48 xl:h-48 object-contain select-none pointer-events-none drop-shadow-[0_0_40px_rgba(244,196,0,0.9)]" />
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#003E87] via-[#F4C400] to-[#003E87]" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-slate-900/80 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <div className="font-display text-2xl font-bold leading-snug text-white max-w-md drop-shadow-lg">
            Utility-scale solar estimating &amp; CRM.
          </div>
        </div>
      </div>
    </div>
  );
}
